import { HttpResponse } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';

export interface MultipartPart {
  PartNumber: number;
  ETag: string;
}

export interface UploadPartsOptions {
  /** Progresso 0–100, calculado por partes concluídas (não pela ordem). */
  onProgress?: (percent: number) => void;
  /**
   * 6 = teto prático do browser em HTTP/1.1 (limite de conexões simultâneas por host).
   * Subir além disso não ganha nada enquanto o backend não for HTTP/2 ou não usarmos
   * presigned URL direto ao S3.
   */
  concurrency?: number;
  maxRetries?: number;
  /** Injetável nos testes para não esperar o backoff de verdade. */
  delay?: (ms: number) => Promise<void>;
}

const defaultDelay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Envia as partes de um upload multipart em paralelo (pool de concorrência) com retry/backoff.
 *
 * As partes são fatiadas do `file` em blocos de `partSize` e cada PUT é feito via `putPart`.
 * Retorna a lista de partes ordenada por `PartNumber`, pronta para o "complete".
 * Compartilhado por mapas/logs (`FileService`) e mídia social (`SocialService`).
 */
export async function uploadPartsConcurrently(
  file: File,
  partSize: number,
  putPart: (partNumber: number, chunk: Blob) => Observable<HttpResponse<any>>,
  options: UploadPartsOptions = {}
): Promise<MultipartPart[]> {
  const { onProgress, concurrency = 6, maxRetries = 3, delay = defaultDelay } = options;
  const totalParts = Math.ceil(file.size / partSize);
  const parts: MultipartPart[] = new Array(totalParts);
  let completed = 0;
  let nextIndex = 0;

  const worker = async (): Promise<void> => {
    // `nextIndex++` é atômico entre awaits (JS single-thread), então não há corrida na fila.
    for (let i = nextIndex++; i < totalParts; i = nextIndex++) {
      const partNumber = i + 1;
      const start = i * partSize;
      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);

      for (let attempt = 0; ; attempt++) {
        try {
          const resp = await lastValueFrom(putPart(partNumber, chunk));
          const etag = resp.headers.get('Etag') ?? resp.headers.get('ETag');
          if (!etag) {
            throw new Error(`ETag ausente na parte ${partNumber}`);
          }
          parts[i] = { PartNumber: partNumber, ETag: etag.replace(/"/g, '') };
          break;
        } catch (err) {
          if (attempt >= maxRetries) {
            throw err;
          }
          await delay(500 * Math.pow(2, attempt)); // 500ms, 1s, 2s...
        }
      }

      completed++;
      onProgress?.(Math.round((completed / totalParts) * 100));
    }
  };

  const workerCount = Math.max(1, Math.min(concurrency, totalParts));
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  return parts;
}
